import { useReactiveVar } from '@apollo/client/react/hooks/useReactiveVar';

/**
 * Trackers
 */
import { activityTracker } from '@astrumu/core-ui-logger';

/**
 * Hooks
 */
import usePredictionFilters from '../usePredictionFilters';

/**
 * Utils & Types
 */
import { firstCharToUppercase } from '@/lib/utils';

import { currentUserVar } from '@/apollo/cache/variables';

interface Params {
  name?: string;
}

const PAGE_NAME = 'EmToolkitMBA';

function useTracking(trackingParams?: Params) {
  const { predictionFilters } = usePredictionFilters();

  const { userId, email, firstName, lastName } = useReactiveVar(currentUserVar);

  /**
   * Gets href for analytics
   * Helps if url has a hash at the end of url
   * e.g. ?jobId=212
   */
  function getHref() {
    const {
      location: { pathname, search }
    } = window;

    return `${pathname}${search}`;
  }

  /**
   * Tracks page initialization
   */
  function trackInitialization(component?: string) {
    const href = getHref();
    const pageName = component ? firstCharToUppercase(component) : PAGE_NAME;

    const eventFullName = `${pageName}.Initialization`;

    activityTracker.trackPageView(href, eventFullName);
  }

  function trackLoadMore(params: { element: string; param?: string }) {
    const { element } = params;
    const href = getHref();
    const pageName = trackingParams?.name || PAGE_NAME;

    const eventFullName = `${pageName}.${element}ScrollDown.LoadMore${element}`;

    activityTracker.trackPageView(href, eventFullName);
  }

  /**
   * Decorates track click function
   */
  function decorateClick(
    params: {
      element: string;
      param?: string;
      conversionId?: string | null;
    },
    clickType: string
  ) {
    const { element, conversionId, param = '' } = params;

    const href = getHref();
    const pageName = trackingParams?.name || PAGE_NAME;

    const eventFullName = `${pageName}.${element}.${element}${clickType}`;

    activityTracker.trackClick(href, eventFullName, param, {
      additionalParams: {
        conversionId,
        predictionFilters: {
          ...predictionFilters,
          userId
        }
      },
      user: {
        id: userId,
        email,
        lastName: lastName || '',
        firstName: firstName || ''
      }
    });
  }

  /**
   * Tracks click
   * @param {Object} params
   */
  function trackClick(params: {
    element: string;
    param?: string;
    conversionId?: string | null;
  }) {
    decorateClick(params, 'Click');
  }

  /**
   * Tracks mouse press down
   * @param {Object} params
   */
  function trackMousePressDown(params: { element: string; param?: string }) {
    decorateClick(params, 'MousePressDown');
  }

  /**
   * Tracks drag and drop moving
   * @param {Object} params
   */
  function trackDragAndDrop(params: { element: string; param?: string }) {
    decorateClick(params, 'DragAndDropMove');
  }

  /**
   * Tracks page closing
   * Especially popup
   */
  function trackOutbound(popupName: string) {
    const href = getHref();
    const eventFullName = `${popupName}.ClosingPopup`;
    activityTracker.trackOutbound(href, eventFullName);
  }

  return {
    trackClick,
    trackOutbound,
    trackLoadMore,
    trackDragAndDrop,
    trackMousePressDown,
    trackInitialization
  };
}

export default useTracking;
